import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatBrlCurrency } from "@/lib/price";
import { PercentInput } from "./PercentInput";
import {
  formatDerivedPercentInput,
  parsePercentInput,
  resolvePercentValue,
  storeDefaultPlaceholder,
  type StorePricingMode,
} from "@/lib/pricing";
import { usePricingPreview } from "../hooks/usePricingPreview";
import { usePricingPercentSync } from "../hooks/usePricingPercentSync";

export interface PricingHelperPanelProps {
  effectiveCostCents: number | null;
  pricingMode?: StorePricingMode | null;
  markupPercent: string;
  targetNetMarginPercent: string;
  taxPercent: string;
  storeDefaultMarkupPercent?: number | null;
  storeDefaultTargetNetMarginPercent?: number | null;
  storeDefaultTaxPercent?: number | null;
  onPricingPercentsChange: (values: {
    markupPercent: string;
    targetNetMarginPercent: string;
  }) => void;
  onTaxPercentChange: (value: string) => void;
  onUseSuggestedPrice: (priceCents: number) => void;
  embedded?: boolean;
}

function formatPercent(value: number): string {
  return value.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function usingStoreDefault(input: string): boolean {
  return input.trim() === "";
}

interface PricingPercentFieldConfig {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  usesStoreDefault: boolean;
  onChange: (value: string) => void;
}

function PricingPercentField({ id, label, value, placeholder, usesStoreDefault, onChange }: PricingPercentFieldConfig) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <label
        htmlFor={id}
        className="text-xs font-light tracking-widest uppercase leading-snug text-primary-600 dark:text-primary-400"
      >
        {label}
      </label>
      <PercentInput
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      <p className="min-h-4 text-xs font-light text-primary-500">
        {usesStoreDefault ? "Usando padrão da loja" : "\u00A0"}
      </p>
    </div>
  );
}

export function PricingHelperPanel({
  effectiveCostCents,
  pricingMode = "target_net_margin",
  markupPercent,
  targetNetMarginPercent,
  taxPercent,
  storeDefaultMarkupPercent,
  storeDefaultTargetNetMarginPercent,
  storeDefaultTaxPercent,
  onPricingPercentsChange,
  onTaxPercentChange,
  onUseSuggestedPrice,
  embedded = false,
}: PricingHelperPanelProps) {
  const effectiveMarkup = resolvePercentValue(markupPercent, storeDefaultMarkupPercent);
  const effectiveMargin = resolvePercentValue(
    targetNetMarginPercent,
    storeDefaultTargetNetMarginPercent
  );
  const effectiveTax = resolvePercentValue(taxPercent, storeDefaultTaxPercent);

  const hasCost = effectiveCostCents != null && effectiveCostCents > 0;
  const hasMarkup = effectiveMarkup != null && effectiveMarkup >= 0;
  const useMarginMode =
    pricingMode === "target_net_margin" &&
    effectiveMargin != null &&
    effectiveMargin >= 0 &&
    effectiveMargin < 100;

  const previewParams = useMemo(() => {
    if (!hasCost || effectiveCostCents == null) return null;

    return {
      effectiveCostCents,
      pricingMode,
      markupPercent,
      targetNetMarginPercent,
      taxPercent,
      storeDefaultMarkupPercent,
      storeDefaultTargetNetMarginPercent,
      storeDefaultTaxPercent,
    };
  }, [
    effectiveCostCents,
    hasCost,
    markupPercent,
    pricingMode,
    storeDefaultMarkupPercent,
    storeDefaultTargetNetMarginPercent,
    storeDefaultTaxPercent,
    targetNetMarginPercent,
    taxPercent,
  ]);

  const { breakdown } = usePricingPreview(previewParams);
  const { syncFromMarkup, syncFromMargin } = usePricingPercentSync();

  const markupAmountCents =
    breakdown != null && effectiveCostCents != null
      ? breakdown.basePriceCents - effectiveCostCents
      : null;
  const hasTax = effectiveTax != null && effectiveTax > 0;
  const displayMarkupPercent = useMarginMode ? breakdown?.markupPercent : effectiveMarkup;

  const missingPricing =
    pricingMode === "target_net_margin"
      ? effectiveMargin == null && !hasMarkup
      : !hasMarkup;

  const usingAnyStoreDefault =
    (usingStoreDefault(markupPercent) && storeDefaultMarkupPercent != null) ||
    (usingStoreDefault(targetNetMarginPercent) && storeDefaultTargetNetMarginPercent != null) ||
    (usingStoreDefault(taxPercent) && storeDefaultTaxPercent != null);

  const handleMarkupChange = (value: string) => {
    if (value.trim() === "") {
      onPricingPercentsChange({ markupPercent: "", targetNetMarginPercent: "" });
      return;
    }

    if (!hasCost || effectiveCostCents == null) {
      onPricingPercentsChange({ markupPercent: value, targetNetMarginPercent });
      return;
    }

    const markup = parsePercentInput(value);
    if (markup == null || markup < 0 || value.endsWith(",")) {
      onPricingPercentsChange({ markupPercent: value, targetNetMarginPercent });
      return;
    }

    onPricingPercentsChange({ markupPercent: value, targetNetMarginPercent });
    syncFromMarkup(effectiveCostCents, markup, effectiveTax, (margin) => {
      if (margin == null) return;

      onPricingPercentsChange({
        markupPercent: value,
        targetNetMarginPercent: formatDerivedPercentInput(margin),
      });
    });
  };

  const handleMarginChange = (value: string) => {
    if (value.trim() === "") {
      onPricingPercentsChange({ markupPercent: "", targetNetMarginPercent: "" });
      return;
    }

    if (!hasCost || effectiveCostCents == null) {
      onPricingPercentsChange({ markupPercent, targetNetMarginPercent: value });
      return;
    }

    const margin = parsePercentInput(value);
    if (margin == null || margin < 0 || value.endsWith(",")) {
      onPricingPercentsChange({ markupPercent, targetNetMarginPercent: value });
      return;
    }

    onPricingPercentsChange({ markupPercent, targetNetMarginPercent: value });
    syncFromMargin(effectiveCostCents, margin, effectiveTax, (derivedMarkup) => {
      if (derivedMarkup == null) return;

      onPricingPercentsChange({
        markupPercent: formatDerivedPercentInput(derivedMarkup),
        targetNetMarginPercent: value,
      });
    });
  };

  const pricingFields: PricingPercentFieldConfig[] = [
    {
      id: "product-markup-percent",
      label: "Markup sobre custo (%)",
      value: markupPercent,
      placeholder: storeDefaultPlaceholder(storeDefaultMarkupPercent),
      usesStoreDefault: usingStoreDefault(markupPercent) && storeDefaultMarkupPercent != null,
      onChange: handleMarkupChange,
    },
    {
      id: "product-target-net-margin",
      label: "Margem líquida desejada (%)",
      value: targetNetMarginPercent,
      placeholder: storeDefaultPlaceholder(storeDefaultTargetNetMarginPercent),
      usesStoreDefault:
        usingStoreDefault(targetNetMarginPercent) && storeDefaultTargetNetMarginPercent != null,
      onChange: handleMarginChange,
    },
    {
      id: "product-tax-percent",
      label: "Alíquota efetiva de impostos (%)",
      value: taxPercent,
      placeholder: storeDefaultPlaceholder(storeDefaultTaxPercent),
      usesStoreDefault: usingStoreDefault(taxPercent) && storeDefaultTaxPercent != null,
      onChange: onTaxPercentChange,
    },
  ];

  const fieldLabelClassName =
    "text-xs font-light tracking-widest uppercase leading-snug text-primary-600 dark:text-primary-400";

  return (
    <section
      className={cn(
        "space-y-4",
        !embedded && "border border-primary-200 dark:border-primary-800 p-6"
      )}
      data-testid="pricing-helper-panel"
    >
      {!embedded ? (
        <h3 className="text-xs font-light tracking-widest uppercase text-primary-600 dark:text-primary-400">
          Precificação
        </h3>
      ) : null}

      {!hasCost ? (
        <p className="text-sm font-light text-primary-600 dark:text-primary-400">
          Informe o custo manualmente ou adicione insumos na receita para ver markup, impostos,
          margem e preço sugerido.
        </p>
      ) : (
        <>
          <div className="space-y-4 sm:hidden">
            {pricingFields.map((field) => (
              <PricingPercentField key={field.id} {...field} />
            ))}
          </div>

          <div className="hidden sm:grid sm:grid-cols-3 sm:gap-x-4 sm:gap-y-2">
            {pricingFields.map((field) => (
              <label key={`${field.id}-label`} htmlFor={field.id} className={fieldLabelClassName}>
                {field.label}
              </label>
            ))}
            {pricingFields.map((field) => (
              <PercentInput
                key={field.id}
                id={field.id}
                value={field.value}
                onChange={field.onChange}
                placeholder={field.placeholder}
              />
            ))}
            {pricingFields.map((field) => (
              <p key={`${field.id}-hint`} className="min-h-4 text-xs font-light text-primary-500">
                {field.usesStoreDefault ? "Usando padrão da loja" : "\u00A0"}
              </p>
            ))}
          </div>

          <p className="text-xs font-light text-primary-500 dark:text-primary-500">
            Deixe em branco para herdar o padrão da loja. Preencha para sobrescrever neste produto.
            Markup e margem são sincronizados automaticamente. O modo de precificação (markup ou margem)
            é definido em Minha loja.
          </p>

          {missingPricing ? (
            <p className="text-sm font-light text-primary-600 dark:text-primary-400">
              Configure markup ou margem na loja ou neste produto para calcular o preço sugerido.
            </p>
          ) : breakdown == null ? (
            <p className="text-sm font-light text-destructive">
              Margem e impostos somam 100% ou mais — ajuste os valores para calcular o preço
              sugerido.
            </p>
          ) : (
            <>
              {usingAnyStoreDefault && (
                <p className="text-xs font-light text-primary-500">
                  Cálculo inclui valores padrão da loja onde o produto não sobrescreve.
                </p>
              )}

              <dl className="space-y-3 text-sm font-light">
                <div className="flex justify-between gap-4">
                  <dt className="text-primary-600 dark:text-primary-400">Custo efetivo</dt>
                  <dd className="text-primary-900 dark:text-white">
                    {formatBrlCurrency(effectiveCostCents)}
                  </dd>
                </div>

                {displayMarkupPercent != null && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-primary-600 dark:text-primary-400">
                      Markup sobre custo ({formatPercent(displayMarkupPercent)}%)
                      {useMarginMode ? " — equivalente" : ""}
                      {usingStoreDefault(markupPercent) && storeDefaultMarkupPercent != null
                        ? " — padrão da loja"
                        : ""}
                    </dt>
                    <dd className="text-primary-900 dark:text-white">
                      {markupAmountCents != null ? formatBrlCurrency(markupAmountCents) : "—"}
                    </dd>
                  </div>
                )}

                <div className="flex justify-between gap-4">
                  <dt className="text-primary-600 dark:text-primary-400">
                    Preço base (antes dos impostos)
                  </dt>
                  <dd className="text-primary-900 dark:text-white">
                    {formatBrlCurrency(breakdown.basePriceCents)}
                  </dd>
                </div>

                {hasTax ? (
                  <div className="flex justify-between gap-4">
                    <dt className="text-primary-600 dark:text-primary-400">
                      Impostos embutidos ({formatPercent(effectiveTax!)}%)
                      {usingStoreDefault(taxPercent) && storeDefaultTaxPercent != null
                        ? " — padrão da loja"
                        : ""}
                    </dt>
                    <dd className="text-primary-900 dark:text-white">
                      {formatBrlCurrency(breakdown.embeddedTaxCents)}
                    </dd>
                  </div>
                ) : (
                  <div className="flex justify-between gap-4">
                    <dt className="text-primary-600 dark:text-primary-400">Impostos embutidos</dt>
                    <dd className="text-primary-500 dark:text-primary-500">Não configurado</dd>
                  </div>
                )}

                <div className="flex justify-between gap-4">
                  <dt className="text-primary-600 dark:text-primary-400">Lucro líquido</dt>
                  <dd className="text-primary-900 dark:text-white">
                    {formatBrlCurrency(breakdown.netProfitCents)}
                  </dd>
                </div>

                <div className="flex justify-between gap-4">
                  <dt className="text-primary-600 dark:text-primary-400">Margem líquida (sobre preço)</dt>
                  <dd className="text-primary-900 dark:text-white">
                    {formatPercent(breakdown.netMarginPercent)}%
                  </dd>
                </div>

                <div className="flex justify-between gap-4 border-t border-primary-200 dark:border-primary-800 pt-3">
                  <dt className="text-primary-800 dark:text-white">Preço sugerido</dt>
                  <dd className="text-primary-900 dark:text-white">
                    {formatBrlCurrency(breakdown.suggestedPriceCents)}
                  </dd>
                </div>
              </dl>

              <Button
                type="button"
                variant="outline"
                onClick={() => onUseSuggestedPrice(breakdown.suggestedPriceCents)}
              >
                Usar preço sugerido
              </Button>
            </>
          )}
        </>
      )}
    </section>
  );
}
